<template>
  <div>
    <v-container>
      <v-card class="tn-grid">
        <div class="tn-grid__head">
          <div class="tn-grid__heading">
            <span class="tn-grid__icon"><v-icon size="22">mdi-file-document-multiple-outline</v-icon></span>
            <div class="tn-grid__titles">
              <h2 class="tn-grid__title">App pages</h2>
              <div class="tn-grid__count">{{ current.hint }}</div>
            </div>
          </div>
          <div class="tn-grid__tools">
            <v-btn v-if="current.key === 'faq'" depressed color="primary" @click="openAdd">
              <v-icon left size="20">mdi-plus</v-icon>
              Add question
            </v-btn>
          </div>
        </div>

        <v-tabs v-model="tab" color="primary" class="px-4" show-arrows>
          <v-tab v-for="item in tabs" :key="item.key">
            {{ item.label }}
            <span v-if="item.key === 'faq'" class="tn-app-pages__count">{{ faqs.length }}</span>
          </v-tab>
        </v-tabs>
        <v-divider></v-divider>

        <div v-if="loading" class="pa-6">
          <v-progress-linear indeterminate color="primary"></v-progress-linear>
        </div>

        <!-- Privacy policy / Terms of use -->
        <div v-else-if="page" :key="page.slug" class="pa-4 pa-sm-6">
          <v-text-field
            v-model="page.title"
            label="Title"
            outlined
            counter="255"
            :rules="[v => !!(v && v.trim()) || 'Title is required', v => !v || v.length <= 255 || 'At most 255 characters']"
          ></v-text-field>
          <editor v-model="page.content" :api-key="tinymceKey" :init="editorInit" />
          <div class="tn-app-pages__footer">
            <span class="tn-app-pages__muted">
              The app shows paragraphs, headings, lists, bold and italic; other formatting is dropped.
              <template v-if="page.updated_at"><br>Last saved {{ formatDate(page.updated_at) }}</template>
            </span>
            <v-spacer></v-spacer>
            <v-btn depressed color="primary" :loading="saving" @click="savePage">Save page</v-btn>
          </div>
        </div>

        <!-- FAQ -->
        <v-data-table
          v-else-if="current.key === 'faq'"
          :headers="faqHeaders"
          :items="faqs"
          item-key="id"
          :items-per-page="-1"
          hide-default-footer
          :mobile-breakpoint="0"
          class="tn-grid__table"
        >
          <template v-slot:item.question="{ item }">
            <div class="font-weight-medium">{{ item.question }}</div>
            <div class="tn-app-pages__muted tn-app-pages__answer">{{ item.answer }}</div>
          </template>
          <template v-slot:item.is_active="{ item }">
            <v-chip small :class="['tn-status-chip', 'tn-status-chip--' + (item.is_active ? 'success' : 'grey')]">
              {{ item.is_active ? 'Shown' : 'Hidden' }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn icon small color="primary" aria-label="Edit question" @click="openEdit(item)">
              <v-icon size="20">mdi-pencil-outline</v-icon>
            </v-btn>
            <v-btn icon small color="error" aria-label="Delete question" @click="removeFaq(item)">
              <v-icon size="20">mdi-trash-can-outline</v-icon>
            </v-btn>
          </template>
          <template v-slot:no-data>
            <div class="tn-empty">
              <v-icon size="40">mdi-frequently-asked-questions</v-icon>
              <span>No questions yet: the app shows an empty FAQ page.</span>
            </div>
          </template>
        </v-data-table>

        <!-- Contact & support -->
        <v-form v-else ref="contactForm" class="pa-4 pa-sm-6" @submit.prevent="saveContact">
          <div class="tn-app-pages__section">Support</div>
          <p class="tn-app-pages__muted">
            Shown on Help &amp; Support and Contact us, and on every booking for cancellations and changes.
            An empty field uses the server setting.
          </p>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field v-model="contact.support.email" label="E-mail" outlined :rules="[emailRule]"></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="contact.support.phone" label="Phone" placeholder="+20 2 3338 4000" outlined :rules="[phoneRule]"></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model="contact.support.whatsapp" label="WhatsApp" placeholder="+201080107180" outlined :rules="[phoneRule]"></v-text-field>
            </v-col>
          </v-row>

          <div class="tn-app-pages__section">Offices</div>
          <p v-if="!contact.offices.length" class="tn-app-pages__muted">No offices: Contact us shows the support details only.</p>
          <v-row v-for="(office, i) in contact.offices" :key="i" dense>
            <v-col cols="12" md="3">
              <v-text-field v-model="office.name" label="Name" outlined :rules="[v => !!(v && v.trim()) || 'Name is required']"></v-text-field>
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field v-model="office.address" label="Address" outlined counter="255"></v-text-field>
            </v-col>
            <v-col cols="10" md="3">
              <v-text-field v-model="office.phone" label="Phone" outlined :rules="[phoneRule]"></v-text-field>
            </v-col>
            <v-col cols="2" md="1" class="tn-app-pages__remove">
              <v-btn icon color="error" aria-label="Remove office" @click="contact.offices.splice(i, 1)">
                <v-icon>mdi-trash-can-outline</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-btn text color="primary" :disabled="contact.offices.length >= 10" @click="contact.offices.push({ name: '', address: '', phone: '' })">
            <v-icon left>mdi-plus</v-icon>
            Add office
          </v-btn>

          <div class="tn-app-pages__footer">
            <v-spacer></v-spacer>
            <v-btn depressed color="primary" :loading="saving" @click="saveContact">Save contact details</v-btn>
          </div>
        </v-form>
      </v-card>
    </v-container>

    <!-- One dialog for add and edit: it always opens with the question it is for (or empty). -->
    <v-dialog v-model="dialog" max-width="640" scrollable :persistent="saving">
      <v-card>
        <v-card-title class="text-h6">{{ isEdit ? 'Edit question' : 'Add question' }}</v-card-title>
        <v-card-text class="pt-2">
          <v-form ref="faqForm" @submit.prevent="saveFaq">
            <v-textarea
              v-model="form.question"
              label="Question"
              outlined
              rows="2"
              auto-grow
              counter="500"
              :rules="[v => !!(v && v.trim()) || 'Question is required', v => !v || v.length <= 500 || 'At most 500 characters']"
            ></v-textarea>
            <v-textarea
              v-model="form.answer"
              label="Answer"
              hint="Plain text; new lines are kept."
              persistent-hint
              outlined
              rows="5"
              auto-grow
              counter="5000"
              :rules="[v => !!(v && v.trim()) || 'Answer is required', v => !v || v.length <= 5000 || 'At most 5000 characters']"
            ></v-textarea>
            <v-row dense class="mt-2">
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="form.sort"
                  label="Order"
                  type="number"
                  min="0"
                  hint="Smaller first"
                  persistent-hint
                  outlined
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="8">
                <v-switch v-model="form.is_active" inset label="Show in the app" class="mt-2"></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-btn text :disabled="saving" @click="dialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn depressed color="primary" :loading="saving" @click="saveFaq">{{ isEdit ? 'Save changes' : 'Add' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { appInfo, appInfoPage, appInfoFaqs, appInfoFaq, appInfoContact, headers } from '../../links'
import Editor from '@tinymce/tinymce-vue'
import Swal from 'sweetalert2'

const tabs = [
  { key: 'privacy-policy', label: 'Privacy policy', hint: 'Profile → Privacy Policy in the app.' },
  { key: 'terms-of-use', label: 'Terms of use', hint: 'Profile → Terms of Use in the app.' },
  { key: 'faq', label: 'FAQ', hint: 'Profile → FAQ: the shown questions, in order.' },
  { key: 'contact', label: 'Contact & support', hint: 'Profile → Contact us and Help & Support, and the support contacts on bookings.' }
]

const PHONE = /^\+[0-9][0-9 ().-]{5,29}$/

function emptyContact () {
  return { support: { email: '', phone: '', whatsapp: '' }, offices: [] }
}

export default {
  components: { editor: Editor },
  data () {
    return {
      tabs,
      tab: 0,
      loading: true,
      saving: false,
      pages: [],
      faqs: [],
      contact: emptyContact(),
      dialog: false,
      isEdit: false,
      id: null,
      form: { question: '', answer: '', sort: 0, is_active: true },
      faqHeaders: [
        { text: 'Question', value: 'question', sortable: false },
        { text: 'Order', value: 'sort' },
        { text: 'Status', value: 'is_active', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' }
      ],
      tinymceKey: 'x21puj7j5tdxkuct1ma1pa63pwru6yf0skeh4huvvgxv4f5x',
      // Only what the app renders (docs/mobile-app/api.md, GET /pages/{slug}).
      editorInit: {
        height: 560,
        menubar: false,
        plugins: ['lists paste'],
        toolbar: 'undo redo | formatselect | bold italic | bullist numlist | removeformat',
        block_formats: 'Paragraph=p; Heading=h3; Subheading=h4',
        valid_elements: 'p,br,ul,ol,li,strong/b,em/i,h3,h4',
        content_style: 'body { font-family: Roboto, Arial, sans-serif; font-size: 14px; }'
      },
      emailRule: v => !v || /^\S+@\S+\.\S+$/.test(v.trim()) || 'A valid e-mail address',
      phoneRule: v => !v || PHONE.test(v.trim()) || 'With + and the country code, e.g. +20 2 3338 4000'
    }
  },
  computed: {
    current () {
      return this.tabs[this.tab] || this.tabs[0]
    },
    page () {
      return this.pages.find(page => page.slug === this.current.key) || null
    }
  },
  methods: {
    notify (color, text) {
      this.$root.$emit('tn-notify', { color, text })
    },
    formatDate (iso) {
      return new Date(iso).toLocaleString()
    },
    // The API answers 422 with { errors: { field: [message] } } and no `message`.
    errorText (err, fallback) {
      const body = (err && err.body) || {}
      const errors = body.errors && Object.values(body.errors)
      if (errors && errors.length) return [].concat(errors[0])[0]
      return body.message || fallback
    },
    auth () {
      return { headers: headers(this.$cookies.get('userToken')) }
    },
    setContact (contact) {
      const support = (contact && contact.support) || {}
      this.contact = {
        support: { email: support.email || '', phone: support.phone || '', whatsapp: support.whatsapp || '' },
        offices: ((contact && contact.offices) || []).map(office => ({ name: office.name || '', address: office.address || '', phone: office.phone || '' }))
      }
    },
    savePage () {
      const page = this.page
      if (!page.title || !page.title.trim()) {
        this.notify('error', 'Title is required')
        return
      }
      this.saving = true
      this.$http.put(appInfoPage(page.slug), { title: page.title.trim(), content: page.content }, this.auth()).then(response => {
        this.saving = false
        Object.assign(page, response.body.data)
        this.notify('success', 'Saved: the app shows it now')
      }, err => {
        this.saving = false
        this.notify('error', this.errorText(err, 'Could not save'))
      })
    },
    openForm (isEdit, values, id) {
      this.isEdit = isEdit
      this.id = id
      this.form = { question: '', answer: '', sort: 0, is_active: true, ...values }
      this.dialog = true
      this.$nextTick(() => this.$refs.faqForm && this.$refs.faqForm.resetValidation())
    },
    openAdd () {
      const next = this.faqs.reduce((max, item) => Math.max(max, item.sort), 0) + 1
      this.openForm(false, { sort: next }, null)
    },
    openEdit (item) {
      this.openForm(true, { question: item.question, answer: item.answer, sort: item.sort, is_active: item.is_active }, item.id)
    },
    saveFaq () {
      if (!this.$refs.faqForm.validate()) return
      const body = {
        question: this.form.question.trim(),
        answer: this.form.answer.trim(),
        sort: Number(this.form.sort) || 0,
        is_active: this.form.is_active
      }
      this.saving = true
      const request = this.isEdit ? this.$http.put(appInfoFaq(this.id), body, this.auth()) : this.$http.post(appInfoFaqs, body, this.auth())
      request.then(() => {
        this.saving = false
        this.dialog = false
        this.notify('success', this.isEdit ? 'Saved' : 'Added')
        this.loadFaqs()
      }, err => {
        this.saving = false
        this.notify('error', this.errorText(err, 'Could not save'))
      })
    },
    removeFaq (item) {
      Swal.fire({
        title: 'Delete this question?',
        text: 'It disappears from the app at once.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      }).then(result => {
        if (!result.isConfirmed) return
        this.$http.delete(appInfoFaq(item.id), this.auth()).then(() => {
          this.notify('success', 'Deleted')
          this.loadFaqs()
        }, err => {
          this.notify('error', this.errorText(err, 'Could not delete'))
        })
      })
    },
    saveContact () {
      if (!this.$refs.contactForm.validate()) return
      const trim = value => (value || '').trim()
      const body = {
        support: { email: trim(this.contact.support.email), phone: trim(this.contact.support.phone), whatsapp: trim(this.contact.support.whatsapp) },
        offices: this.contact.offices.map(office => ({ name: trim(office.name), address: trim(office.address), phone: trim(office.phone) }))
      }
      this.saving = true
      this.$http.put(appInfoContact, body, this.auth()).then(response => {
        this.saving = false
        this.setContact(response.body.data)
        this.notify('success', 'Saved: the app shows it now')
      }, err => {
        this.saving = false
        this.notify('error', this.errorText(err, 'Could not save'))
      })
    },
    load () {
      return this.$http.get(appInfo, this.auth()).then(response => response.body.data || {}, err => {
        this.notify('error', this.errorText(err, 'Could not load the app pages'))
        return null
      })
    },
    // Only the FAQ: unsaved page or contact edits stay on screen.
    loadFaqs () {
      this.load().then(data => {
        if (data) this.faqs = data.faqs || []
      })
    }
  },
  created () {
    this.load().then(data => {
      this.loading = false
      if (!data) return
      this.pages = data.pages || []
      this.faqs = data.faqs || []
      this.setContact(data.contact)
    })
  }
}
</script>

<style lang="scss" scoped>
.tn-app-pages__count {
  margin-left: 6px;
  color: var(--tn-muted);
  font-size: 12px;
}

.tn-app-pages__muted {
  color: var(--tn-muted);
  font-size: 12.5px;
}

.tn-app-pages__answer {
  display: -webkit-box;
  max-width: 640px;
  overflow: hidden;
  white-space: pre-line;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.tn-app-pages__section {
  margin: 4px 0 8px;
  color: var(--tn-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.tn-app-pages__remove {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 10px !important;
}

.tn-app-pages__footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}
</style>
